import { Component, OnDestroy, OnInit } from '@angular/core';
import { SigningService } from 'src/app/services/signing.service';

// How the page ends up looking to the signer. Everything other than a reported outcome is a state the signer
// has to be told about: this page is the only thing they see, and a signing action they believe was recorded
// but was not is worse than an error.
export type ESignVendorConfirmationStatus =
  'reporting' | 'signed' | 'declined' | 'cancelled' | 'notRecorded';

const TOKEN_RESPONSE_TIMEOUT_MS = 10000;

// Landing page for vendors that pin their end-of-signing redirect to the signing order instead of to one
// signer's signing session - SignNow - so the redirect url carries no task, token or order of its own. It
// renders inside the signing iframe and hands the outcome to the dialog hosting that iframe, which knows
// which task was being signed.
//
// The api is configured with one static host for this page, so for a company whose admin portal is served
// from another domain this page is cross-origin to that portal and cannot hand the outcome over that way: a
// cross-origin parent can neither be targeted by origin nor be trusted to be a portal at all. It instead asks
// the parent for the signer's auth token and the task being signed - the handshake the Encompass POS eClose
// flow already uses, which needs no cross-site cookie - and reports the outcome to the api itself.
@Component({
    selector: 'e-sign-vendor-confirmation',
    templateUrl: './e-sign-vendor-confirmation.component.html',
    standalone: false
})
export class ESignVendorConfirmationComponent implements OnInit, OnDestroy {

  protected status: ESignVendorConfirmationStatus = 'reporting';

  private _action: string | null = null;
  private _tokenResponseHandlerRegistered: boolean = false;
  private _tokenResponseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly _tokenResponseHandler = (event: MessageEvent) => {
    // Only the window this page asked for a token can answer, and only an answer carrying the task being
    // signed is one this page can act on - the Encompass POS flow answers the same request with a bare token.
    if (event.source !== window.parent) {
      return;
    }
    if (event.data?.authTokenDenied) {
      this.stopWaitingForTokenResponse();
      this.status = 'notRecorded';
      return;
    }
    if (!event.data?.authCompletionToken) {
      return;
    }
    const loanDocTaskId = Number(event.data.eSignCompletionContext?.loanDocTaskId);
    if (!loanDocTaskId) {
      return;
    }

    this.stopWaitingForTokenResponse();
    this.reportSigningOutcome(event.origin, event.data.authCompletionToken, loanDocTaskId);
  };

  constructor(private readonly _signingService: SigningService) { }

  ngOnInit(): void {
    const searchParams = new URLSearchParams(location.search);
    this._action = searchParams.get('event') ?? searchParams.get('esign.status');

    // The vendor redirected the whole window rather than the signing frame, so there is no portal to hand the
    // outcome to and no session to report it with. Nothing can be recorded from here - say so.
    if (window.parent === window) {
      this.status = 'notRecorded';
      return;
    }

    if (this.isParentSameOrigin()) {
      // The dialog hosting this iframe is served from this same admin origin, so target it explicitly
      // instead of '*' to avoid leaking the vendor signing return parameters to any embedding window.
      window.parent.postMessage(
        {
          eSignVendorSigningEnded: true,
          action: this._action,
          vendorQueryParameters: this.getVendorQueryParameters(),
        },
        window.location.origin
      );
      // The dialog reports the outcome and closes this iframe, surfacing its own error if that fails.
      this.status = this.outcomeStatus();
      return;
    }

    this.startWaitingForTokenResponse();
    // The parent's origin is unknown until it answers, and this request carries nothing sensitive, so it is
    // the one message broadcast to the embedding window; everything after this is targeted at that origin.
    window.parent.postMessage({ authTokenRequested: true }, '*');
  }

  ngOnDestroy(): void {
    this.stopWaitingForTokenResponse();
  }

  private reportSigningOutcome = (parentOrigin: string, authToken: string, loanDocTaskId: number) => {
    this._signingService
      .onSignerViewSubmittedWithExternalToken(
        loanDocTaskId,
        this._action,
        authToken,
        this.getVendorQueryParameters()
      )
      .subscribe({
        next: (response) => {
          this.status = this.outcomeStatus();
          window.parent.postMessage(
            {
              shouldCloseEsignDocIframe: true,
              response: response,
            },
            parentOrigin
          );
        },
        error: (error) => {
          this.status = 'notRecorded';
          console.error(error?.error?.message ?? 'E-sign confirmation failed.');
        },
      });
  };

  // What the signer actually did, as the vendor reported it on the redirect. The api reads the same values.
  private outcomeStatus = (): ESignVendorConfirmationStatus => {
    switch (this._action) {
      case 'decline':
        return 'declined';
      case 'cancel':
        return 'cancelled';
      default:
        return 'signed';
    }
  };

  private getVendorQueryParameters = (): { key: string; value: string }[] =>
    Array.from(new URLSearchParams(location.search).entries()).map(([key, value]) => ({ key, value }));

  private isParentSameOrigin = (): boolean => {
    try {
      // Reading the parent's location throws for a cross-origin parent, which is the only way to tell the two
      // apart - a cross-origin window exposes nothing else to compare.
      return window.parent.location.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  private startWaitingForTokenResponse = () => {
    if (this._tokenResponseHandlerRegistered) {
      return;
    }
    window.addEventListener('message', this._tokenResponseHandler, false);
    this._tokenResponseHandlerRegistered = true;
    // A parent that never answers - one that is not one of our portals, or one whose signer is not logged in -
    // would otherwise leave this page claiming an outcome it never reported.
    this._tokenResponseTimeoutId = setTimeout(() => {
      this.stopWaitingForTokenResponse();
      this.status = 'notRecorded';
    }, TOKEN_RESPONSE_TIMEOUT_MS);
  };

  private stopWaitingForTokenResponse = () => {
    if (this._tokenResponseTimeoutId !== null) {
      clearTimeout(this._tokenResponseTimeoutId);
      this._tokenResponseTimeoutId = null;
    }
    if (!this._tokenResponseHandlerRegistered) {
      return;
    }
    window.removeEventListener('message', this._tokenResponseHandler, false);
    this._tokenResponseHandlerRegistered = false;
  };
}
